import React from 'react'

import DashboardContainer from "container/DashboardContainer.js";
import UploadPageContainer from "container/UploadPageContainer.js";
import UnionInfoContainer from "container/UnionInfoContainer.js";
import RevenueContainer from "container/RevenueContainer.js";
import WeeklyTotalPayContainer from "container/WeeklyTotalPayContainer.js";
import WeeklyTotalWinContainer from 'container/WeeklyTotalWinContainer.js';
import TinyClubConfigContainer from 'container/TinyClubConfigContainer.js';
import TinyReportsContainer from 'container/TinyReportsContainer.js';
import TinyUploadContainer from 'container/TinyUploadContainer.js';
import TinyBiweeklyContainer from 'container/TinyBiweeklyContainer.js';
import TinyAnalyticsContainer from 'container/TinyAnalyticsContainer.js';
import TinyCustomSettlementsContainer from 'container/TinyCustomSettlementsContainer.js';
import TinySettlementCenterContainer from 'container/TinySettlementCenterContainer.js';
import TinyPlayerQueryContainer from 'container/TinyPlayerQueryContainer.js';
import TinyGameStatsContainer from 'container/TinyGameStatsContainer.js';
import TinyJPBankerContainer from 'container/TinyJPBankerContainer.js';
import TinyGameLevelDetail from 'views/tiny/TinyGameLevelDetail.js';
import TinyTopPlayers from 'views/tiny/TinyTopPlayers.js';
import TinyAOFAnalysisContainer from 'container/TinyAOFAnalysisContainer.js';
import TinyClubRakeContainer from 'container/TinyClubRakeContainer.js';
import TinyRevenueContainer from 'container/TinyRevenueContainer.js';
import TinyRevenueRulesContainer from 'container/TinyRevenueRulesContainer.js';
import TinyTRRulesContainer from 'container/TinyTRRulesContainer.js';
import TinyOvbRakebackContainer from 'container/TinyOvbRakebackContainer.js';
import TinyEcoBotContainer from 'container/TinyEcoBotContainer.js';
import TinySelfBotContainer from 'container/TinySelfBotContainer.js';
import TinyExploitDetectionContainer from 'container/TinyExploitDetectionContainer.js';
import TinyTicketsContainer from 'container/TinyTicketsContainer.js';
import TinyPlAdjustmentsContainer from 'container/TinyPlAdjustmentsContainer.js';
import TinyMttStatsContainer from 'container/TinyMttStatsContainer.js';
import OAFeeDetailsContainer from 'container/OAFeeDetailsContainer.js';
import TinyWeeklySettlementContainer from 'container/TinyWeeklySettlementContainer.js';
import UsersContainer from 'container/UsersContainer.js';
import AuditLogContainer from 'container/AuditLogContainer.js';

/**
 * 路由配置
 *
 * platform 欄位:
 * - "oa"     : OceanAtlas 專屬
 * - "tiny"   : TinyPoker 專屬
 * - "common" : 跨平台業務數據（永遠顯示，側欄頂部的金色卡片）
 * - "system" : 後臺自身的管理（帳號 / 稽核），側欄最下方的獨立區塊
 *
 * permission 欄位（舊有，不動）:
 *   使用者的 permission 是 admin / normal，admin 看得到全部。
 *
 * requiredCap 欄位（v2.32.0 新增，選填）:
 *   除了 permission 之外**再**要求一個 cap。兩個條件是 AND。
 *   - 'view_revenue'  看得到營收數據
 *   - 'manage_users'  最高權限（使用者管理 + 稽核紀錄）
 *
 *   ⚠️ 前端這層只是「不畫出來 / 不讓你進」，**真正的把關在後端**
 *   （Backend/jwt_protection.py 的 before_request）。前端擋不住 curl。
 *
 * 🔑 掛了 requiredCap: 'view_revenue' 的頁，判準是「這頁沒有那些資料就
 *    不能用」，不是「頁名裡有沒有營收兩個字」：
 *      /revenue         營收數據（總淨營收）
 *      /tiny-revenue    營收總覽
 *      /tiny-analytics  數據分析 —— 首屏卡片就是「本週淨營收」，另有營收趨勢
 *      /dev             OA 儀表板 —— 走 listRevenue / getGamesFee
 *      /oa-fee-details  費用明細 —— 走 getUnionsFee / getMembershipData
 */
/**
 * 側欄分組的顯示順序（v2.39.0）。
 *
 * 每條 tiny 路由的 `group` 只說「我屬於哪一組」，組與組的先後在這裡定，
 * 不靠路由在陣列裡的位置 —— 路由順序另有用途（firstAccessibleRoute 的落地頁），
 * 為了側欄去搬路由會動到那個。沒有 `group` 的路由（OA 的六頁、invisible 的）
 * 直接列在平台標題下面，不分組。
 */
export const SIDEBAR_GROUP_ORDER = ["數據分析", "交收與報表", "規則設定"];

var routes = [
  // ===================== 跨平台 (common) =====================
  {
    path: "/revenue",
    name: "營收數據",
    icon: "fa fa-line-chart",
    component: <RevenueContainer />,
    layout: "/admin",
    permission: "admin",
    requiredCap: "view_revenue",
    platform: "common",
    category: "revenue"
  },

  // ===================== TinyPoker 專屬 =====================
  // ⚠️ 原本這裡有 `/tiny`（數據總覽）—— 2026-09-10 移除。
  //    它是 invisible:true（側欄點不到）、功能與「報表整合中心」重疊，
  //    而且是 index.js 的 fallback 目的地（打根網址會落到一個側欄回不去的頁）。
  //    後端 API 全部有別的頁面在用，所以後端沒有跟著動。
  {
    path: "/tiny-biweekly",
    name: "雙週獎池",
    icon: "fa fa-trophy",
    component: <TinyBiweeklyContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "數據分析",
    category: "bonus"
  },
  
  {
    path: "/tiny-ecobot",
    name: "EcoBot 數據",
    icon: "fa fa-robot",
    component: <TinyEcoBotContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "數據分析",
    category: "analytics"
  },
  {
    // v2.28.3 自營 Bot：我方自己研發的開桌機器人。刻意排在 EcoBot 之後、
    // 但兩者是完全不同的東西（EcoBot 是外部夥伴、我方只分潤）。
    path: "/tiny-self-bot",
    name: "自營Bot 數據",
    icon: "fa fa-android",
    component: <TinySelfBotContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "數據分析",
    category: "analytics"
  },
  {
    path: "/tiny-jp-banker",
    name: "JP Banker 試算",
    icon: "fa fa-calculator",
    component: <TinyJPBankerContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "數據分析",
    category: "analytics"
  },
  {
    path: "/tiny-exploit-detection",
    name: "EcoBot Killer 偵測",
    icon: "fa fa-shield",
    component: <TinyExploitDetectionContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "數據分析",
    category: "analytics"
  },
  {
    path: "/tiny-club-rake",
    name: "B端數據分析",
    icon: "fa fa-pie-chart",
    component: <TinyClubRakeContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "數據分析",
    category: "analytics"
  },
  {
    path: "/tiny-game-stats",
    name: "遊戲種類數據",
    icon: "fa fa-gamepad",
    component: <TinyGameStatsContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "數據分析",
    category: "analytics"
  },
  {
    path: "/tiny-game-level-detail",
    name: "級別詳情",
    icon: "fa fa-search",
    component: <TinyGameLevelDetail />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "數據分析",
    category: "analytics"
  },
  {
    path: "/tiny-top-players",
    name: "級別 Top 玩家",
    icon: "fa fa-users",
    component: <TinyTopPlayers />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "數據分析",
    category: "analytics"
  },
  {
    path: "/tiny-mtt-stats",
    name: "MTT 數據統計",
    icon: "fa fa-trophy",
    component: <TinyMttStatsContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "數據分析",
    category: "analytics"
  },
  {
    path: "/tiny-clubs",
    name: "俱樂部管理",
    icon: "fa fa-building",
    component: <TinyClubConfigContainer />,
    layout: "/admin",
    permission: "admin",
    platform: "tiny",
    group: "規則設定",
    category: "management"
  },
  {
    path: "/tiny-revenue-rules",
    name: "分潤規則",
    icon: "fa fa-sitemap",
    component: <TinyRevenueRulesContainer />,
    layout: "/admin",
    permission: "admin",
    platform: "tiny",
    group: "規則設定",
    category: "management"
  },
  {
    path: "/tiny-tr-rules",
    name: "TR 規則",
    icon: "fa fa-balance-scale",
    component: <TinyTRRulesContainer />,
    layout: "/admin",
    permission: "admin",
    platform: "tiny",
    group: "規則設定",
    category: "management"
  },
  {
    path: "/tiny-settlements",
    name: "額外交收中心",
    icon: "fa fa-gavel",
    component: <TinySettlementCenterContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "交收與報表",
    category: "settlements"
  },
  {
    path: "/tiny-weekly-settlement",
    name: "每週交收總覽",
    icon: "fa fa-exchange",
    component: <TinyWeeklySettlementContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "交收與報表",
    category: "settlements"
  },
  {
    path: "/tiny-reports",
    name: "報表整合中心",
    icon: "fa fa-file-text",
    component: <TinyReportsContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "交收與報表",
    category: "reports"
  },
  {
    // v2.45.0：OVB（718000）代發退水。只算與顯示，不接營收 / 交收 / Excel
    path: "/tiny-ovb-rakeback",
    name: "OVB 代發退水",
    icon: "fa fa-hand-holding-dollar",
    component: <TinyOvbRakebackContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "交收與報表",
    category: "settlements"
  },
  {
    path: "/tiny-tickets",
    name: "票券對賬",
    icon: "fa fa-ticket",
    component: <TinyTicketsContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "交收與報表",
    category: "reports"
  },
  {
    // v2.49.0：ClubGG 原始輸贏 → 聯盟調整（用票補回 / 實體獎勵扣除）→ 最終
    path: "/tiny-pl-adjustments",
    name: "聯盟 P&L 調整",
    icon: "fa fa-scale-balanced",
    component: <TinyPlAdjustmentsContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    group: "交收與報表",
    category: "reports"
  },
  {
    path: "/tiny-upload",
    name: "上傳 Excel",
    icon: "fa fa-cloud-upload",
    component: <TinyUploadContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    category: "reports",
    invisible: true
  },
 
  {
    path: "/tiny-analytics",
    name: "數據分析",
    icon: "fa fa-line-chart",
    component: <TinyAnalyticsContainer />,
    layout: "/admin",
    permission: "normal",
    requiredCap: "view_revenue",
    platform: "tiny",
    category: "analytics",
    invisible: true
  },
  {
    path: "/tiny-players",
    name: "玩家查詢",
    icon: "fa fa-users",
    component: <TinyPlayerQueryContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    category: "analytics",
    invisible: true
  },
  {
    path: "/tiny-revenue",
    name: "營收總覽",
    icon: "fa fa-usd",
    component: <TinyRevenueContainer />,
    layout: "/admin",
    permission: "normal",
    requiredCap: "view_revenue",
    platform: "tiny",
    category: "revenue",
    invisible: true
  },
  {
    path: "/tiny-aof",
    name: "AOF 分析",
    icon: "fa fa-bolt",
    component: <TinyAOFAnalysisContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "tiny",
    category: "analytics",
    invisible: true
  },
  

  // ===================== OceanAtlas 專屬 =====================
  {
    path: "/dev",
    name: "儀表板",
    icon: "fa fa-dashboard",
    component: <DashboardContainer />,
    layout: "/admin",
    permission: "normal",
    requiredCap: "view_revenue",
    platform: "oa",
    category: "main"
  },
  {
    path: "/uploadpage",
    name: "上傳報表",
    icon: "fa fa-upload",
    component: <UploadPageContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "oa",
    category: "reports"
  },
  {
    path: "/unioninfo",
    name: "聯盟資訊",
    icon: "fa fa-percent",
    component: <UnionInfoContainer />,
    layout: "/admin",
    permission: "normal",
    platform: "oa",
    category: "management"
  },
  {
    path: "/weekly-total-pay",
    name: "每周B端交付",
    icon: "fa fa-exchange",
    component: <WeeklyTotalPayContainer/>,
    layout: "/admin",
    permission: "normal",
    platform: "oa",
    category: "settlements"
  },
  {
    path: "/weekly-total-win",
    name: "每周B端輸贏",
    icon: "fa fa-trophy",
    component: <WeeklyTotalWinContainer/>,
    layout: "/admin",
    permission: "normal",
    platform: "oa",
    category: "analytics"
  },
  {
    path: "/oa-fee-details",
    name: "費用明細",
    icon: "fa fa-list-alt",
    component: <OAFeeDetailsContainer/>,
    layout: "/admin",
    permission: "normal",
    requiredCap: "view_revenue",
    platform: "oa",
    category: "analytics"
  },

  // ===================== 系統管理（platform: "system"）=====================
  // 帳號機制是**後臺自身的管理**，不是業務數據 —— 所以不放進頂部那張
  // 跨平台卡片（那裡是「營收數據」），而是沉在側欄最下面、用分隔線隔開
  // 的獨立區塊。整區只有 manage_users 的人看得到；沒有任何一條可見時
  // Sidebar 連標題都不畫。
  {
    path: "/users",
    name: "使用者",
    icon: "fa fa-users",
    component: <UsersContainer />,
    layout: "/admin",
    permission: "admin",
    requiredCap: "manage_users",
    platform: "system",
    category: "admin"
  },
  {
    path: "/audit-log",
    name: "稽核紀錄",
    icon: "fa fa-history",
    component: <AuditLogContainer />,
    layout: "/admin",
    permission: "admin",
    requiredCap: "manage_users",
    platform: "system",
    category: "admin"
  }
];

export default routes;
